import { defineStore } from 'pinia';
import ExclusionGroupService from '@/services/ExclusionGroupService';

export const useExclusionGroupsStore = defineStore('exclusionGroups', {
  state: () => ({
    systemGroups: [],
    customGroups: [],
    blockedGroups: [],
    totalExcludedSymbols: 0,
    currentGroupDetail: null,
    loading: false,
    saving: false,
    error: null,
  }),

  getters: {
    excludedSymbolCount(state) {
      return state.totalExcludedSymbols || 0;
    },
  },

  actions: {
    applyGroupsResponse(data) {
      const payload = data?.data || data || {};
      this.systemGroups = this.normalizeGroups(payload.systemGroups, 'SYSTEM');
      this.customGroups = this.normalizeGroups(payload.customGroups, 'ANORMAL_CUSTOM');
      this.blockedGroups = this.normalizeGroups(payload.blockedGroups, 'BLOCK_CUSTOM');
      this.totalExcludedSymbols = payload.totalExcludedSymbols || 0;
    },

    applyGroupDetail(data) {
      const payload = data?.data || data || {};
      this.currentGroupDetail = {
        ...payload,
        isExcluded: payload.isExcluded ?? payload.excluded ?? false,
        groupType: payload.groupType ?? payload.type ?? payload.group_type,
        symbols: payload.symbols || [],
      };
      return this.currentGroupDetail;
    },

    normalizeGroups(groups, defaultGroupType) {
      return Array.isArray(groups)
        ? groups.map(group => ({
          ...group,
          isExcluded: group.isExcluded ?? group.excluded ?? false,
          isSystem: group.isSystem ?? group.system ?? false,
          groupType: group.groupType ?? group.type ?? group.group_type ?? defaultGroupType,
          symbolCount: group.symbolCount || 0,
        }))
        : [];
    },

    async fetchGroups() {
      this.loading = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.getGroups();
        this.applyGroupsResponse(data);
        return data;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createGroup(payload) {
      this.saving = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.createGroup(payload);
        await this.fetchGroups();
        return data;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async updateGroup(groupId, payload) {
      this.saving = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.updateGroup(groupId, payload);
        await this.fetchGroups();
        if (this.currentGroupDetail?.id === groupId) {
          this.currentGroupDetail = {
            ...this.currentGroupDetail,
            ...data,
          };
        }
        return data;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async deleteGroup(groupId, payload = {}) {
      this.saving = true;
      this.error = null;
      try {
        await ExclusionGroupService.deleteGroup(groupId, payload);
        if (this.currentGroupDetail?.id === groupId) {
          this.currentGroupDetail = null;
        }
        await this.fetchGroups();
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async fetchGroupDetail(groupId) {
      this.loading = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.getGroupDetail(groupId);
        return this.applyGroupDetail(data);
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addSymbols(groupId, symbols) {
      this.saving = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.addSymbols(groupId, symbols);
        this.applyGroupDetail(data);
        await this.fetchGroups();
        return this.currentGroupDetail;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async removeSymbols(groupId, symbols) {
      this.saving = true;
      this.error = null;
      try {
        const data = await ExclusionGroupService.removeSymbols(groupId, symbols);
        this.applyGroupDetail(data);
        await this.fetchGroups();
        return this.currentGroupDetail;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.saving = false;
      }
    },
  },
});
